import type { ApiConfig, StringIdParams } from '@api/types';
import { typedMethodPost, typedMethodPostWithRequiredParams } from '@api/utils';
import type {
  AddDeviceResponseDto,
  SubmitDeviceDetailsRequestDto,
  SubmitDeviceDetailsResponseDto,
  DeleteDeviceResponseDto,
  SearchDevicesRequestDto,
  SearchDevicesResponseDto,
  SubmitDeviceActivationDetailsRequestDto,
  SubmitDeviceActivationDetailsResponseDto,
  AuthenticateDeviceRequestDto,
  AuthenticateDeviceResponseDto,
  CheckPendingActivationsResponseDto,
} from '../../generated/models';

import type {
  AddDeviceResponse,
  SubmitDeviceDetailsResponse,
  DeleteDeviceResponse,
  SearchDevicesResponse,
  SubmitDeviceActivationDetailsResponse,
  AuthenticateDeviceResponse,
  CheckPendingActivationsResponse,
} from '../../domain/types';

import {
  toAddDeviceResponse,
  toSubmitDeviceDetailsResponse,
  toDeleteDeviceResponse,
  toSearchDevicesResponse,
  toSubmitDeviceActivationDetailsResponse,
  toAuthenticateDeviceResponse,
  toCheckPendingActivationsResponse,
} from '../../domain/mappers';

export default ({ baseURL }: ApiConfig) => ({
  getAuthInfo: typedMethodPost<void, AddDeviceResponseDto, AddDeviceResponse>(
    (data) =>
      fetch(`${baseURL}/v1/devices/add`, {
        method: 'POST',
        ...data,
      }),
    toAddDeviceResponse
  ),
  updateDevice: typedMethodPost<SubmitDeviceDetailsRequestDto, SubmitDeviceDetailsResponseDto, SubmitDeviceDetailsResponse>(
    (data) =>
      fetch(`${baseURL}/v1/devices/submitDeviceDetails`, {
        method: 'POST',
        ...data,
      }),
    toSubmitDeviceDetailsResponse
  ),
  deleteDevice: typedMethodPostWithRequiredParams<StringIdParams, DeleteDeviceResponseDto, DeleteDeviceResponse>(
    (data) =>
      fetch(`${baseURL}/v1/devices/delete`, {
        method: 'POST',
        ...data,
        body: JSON.stringify({ deviceId: data.params.id }),
      }),
    toDeleteDeviceResponse
  ),
  getDevices: typedMethodPost<SearchDevicesRequestDto, SearchDevicesResponseDto, SearchDevicesResponse>(
    (data) =>
      fetch(`${baseURL}/v1/devices/search`, {
        method: 'POST',
        ...data,
      }),
    toSearchDevicesResponse
  ),
  submitActivationDetails: typedMethodPost<
    SubmitDeviceActivationDetailsRequestDto,
    SubmitDeviceActivationDetailsResponseDto,
    SubmitDeviceActivationDetailsResponse
  >(
    (data) =>
      fetch(`${baseURL}/v1/devices/submitActivationDetails`, {
        method: 'POST',
        ...data,
      }),
    toSubmitDeviceActivationDetailsResponse
  ),
  getDevicesByID: typedMethodPostWithRequiredParams<string, SearchDevicesResponseDto, SearchDevicesResponse>(
    (data) =>
      fetch(`${baseURL}/v1/devices/search`, {
        method: 'POST',
        ...data,
        body: JSON.stringify({
          filters: {
            deviceId: {
              value: data.params,
              valueType: 'string',
            },
          },
        }),
      }),
    toSearchDevicesResponse
  ),
  authenticateWalboardDevice: typedMethodPost<AuthenticateDeviceRequestDto, AuthenticateDeviceResponseDto, AuthenticateDeviceResponse>(
    (data) =>
      fetch(`${baseURL}/v1/devices/authenticate`, {
        method: 'POST',
        ...data,
      }),
    toAuthenticateDeviceResponse
  ),
  authenticateDevice: typedMethodPost<AuthenticateDeviceRequestDto, AuthenticateDeviceResponseDto, AuthenticateDeviceResponse>(
    (data) =>
      fetch(`${baseURL}/v1/devices/authenticate`, {
        method: 'POST',
        ...data,
      }),
    toAuthenticateDeviceResponse
  ),
  checkPendingActivations: typedMethodPost<void, CheckPendingActivationsResponseDto, CheckPendingActivationsResponse>(
    (data) =>
      fetch(`${baseURL}/v1/devices/checkPendingActivations`, {
        method: 'POST',
        ...data,
      }),
    toCheckPendingActivationsResponse
  ),
});

// Re-export domain types for consumers
export type {
  AddDeviceResponse,
  SubmitDeviceDetailsResponse,
  DeleteDeviceResponse,
  SearchDevicesResponse,
  SubmitDeviceActivationDetailsResponse,
  AuthenticateDeviceResponse,
  CheckPendingActivationsResponse,
} from '../../domain/types';

// Re-export request DTOs (still needed for building requests)
export type {
  SubmitDeviceDetailsRequestDto,
  SearchDevicesRequestDto,
  SubmitDeviceActivationDetailsRequestDto,
  AuthenticateDeviceRequestDto,
} from '../../generated/models';
